import React, { useState, useEffect } from "react";
import {
  getAllDocsSnap,
  deleteDocById,
  queryAllDocsByFieldSnap,
  updateDocById,
} from "../../../utils/firebase/Firebase.utils";
import {
  FaTrashAlt,
  FaCheck,
  FaPause,
  FaExclamationTriangle,
  FaList,
  FaTimes,
} from "react-icons/fa";
import { FaSquareParking } from "react-icons/fa6";
import { LuShoppingBasket, LuDoorOpen } from "react-icons/lu";
import { HiOutlineRefresh } from "react-icons/hi";
import { FiSearch, FiFilter } from "react-icons/fi";
import { IoStatsChart } from "react-icons/io5";
import PageModal from "../Components/PageModal";
import { unitLabel } from "../../../utils/constants";
import { useCompany } from "../../../context/CompanyContext";

function MarketplaceAdmin() {
  const { companyData } = useCompany();
  const [activeCategory, setActiveCategory] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [products, setProducts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [parkingSpaces, setParkingSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showItemDetail, setShowItemDetail] = useState(false);
  const [itemDetail, setItemDetail] = useState(null);

  // Categories for navigation
  const categories = [
    {
      id: 1,
      name: "Products/Services",
      icon: <LuShoppingBasket className="text-lg" />,
      collection: "products",
    },
    {
      id: 2,
      name: `${unitLabel}s for Sale/Long Term Lease`,
      icon: <LuDoorOpen className="text-lg" />,
      collection: "properties",
    },
    ...(companyData?.entityType !== "hoa"
      ? [
          {
            id: 3,
            name: "Parking Spaces",
            icon: <FaSquareParking className="text-lg" />,
            collection: "parkingSpaces",
          },
        ]
      : []),
  ];

  // Fetch data based on active category
  useEffect(() => {
    setLoading(true);

    // Fetch products data
    const unsubscribeProducts = getAllDocsSnap("products", (data) => {
      setProducts(data);
      if (activeCategory === 1) setLoading(false);
    });

    // Fetch properties data
    const unsubscribeProperties = getAllDocsSnap("properties", (data) => {
      setProperties(data);
      if (activeCategory === 2) setLoading(false);
    });

    // Fetch parking spaces data
    const unsubscribeParkingSpaces = getAllDocsSnap("parkingSpaces", (data) => {
      setParkingSpaces(data);
      if (activeCategory === 3) setLoading(false);
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeProducts();
      unsubscribeProperties();
      unsubscribeParkingSpaces();
    };
  }, [activeCategory]);

  // Get active data based on category
  const getActiveData = () => {
    switch (activeCategory) {
      case 1:
        return products;
      case 2:
        return properties;
      case 3:
        return parkingSpaces;
      default:
        return [];
    }
  };

  // Handle search and filtering
  const filteredData = getActiveData().filter((item) => {
    const matchesSearch =
      searchTerm.trim() === ""
        ? true
        : (item.title || item.productTitle || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (item.description || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === "all"
        ? true
        : (item.status || "active").toLowerCase() ===
          filterStatus.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  // Handle item approval
  const handleApprove = async (item) => {
    const collection = categories.find(
      (cat) => cat.id === activeCategory
    ).collection;
    await updateDocById(collection, item.id, { status: "active" });
  };

  // Handle item rejection/suspension
  const handleSuspend = async (item) => {
    const collection = categories.find(
      (cat) => cat.id === activeCategory
    ).collection;
    await updateDocById(collection, item.id, { status: "suspended" });
  };

  // Handle item deletion with confirmation
  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowConfirmation(true);
  };

  const confirmDelete = async () => {
    if (selectedItem) {
      const collection = categories.find(
        (cat) => cat.id === activeCategory
      ).collection;
      await deleteDocById(collection, selectedItem.id);
      setShowConfirmation(false);
      setSelectedItem(null);
    }
  };

  // Handle card click to filter by category
  const handleCardClick = (categoryId) => {
    setActiveCategory(categoryId);
    setFilterStatus("active"); // Show only active items
  };

  // Handle item click to show details
  const handleItemClick = (item) => {
    setItemDetail(item);
    setShowItemDetail(true);
  };

  // Calculate analytics data
  const getAnalyticsData = () => {
    // Calculate count by creator and status
    const creatorStats = {};
    const statusStats = {
      active: {
        products: 0,
        properties: 0,
        ...(companyData?.entityType !== "hoa" ? { parking: 0 } : {}),
      },
      suspended: {
        products: 0,
        properties: 0,
        ...(companyData?.entityType !== "hoa" ? { parking: 0 } : {}),
      },
      deleted: {
        products: 0,
        properties: 0,
        ...(companyData?.entityType !== "hoa" ? { parking: 0 } : {}),
      },
    };

    // Count products
    products.forEach((item) => {
      const creator = item.createdBy || "Unknown";
      const status = item.status || "active";
      if (!creatorStats[creator]) {
        creatorStats[creator] = {
          products: 0,
          properties: 0,
          ...(companyData?.entityType !== "hoa" ? { parking: 0 } : {}),
        };
      }
      creatorStats[creator].products++;
      if (statusStats[status]) {
        statusStats[status].products++;
      }
    });

    // Count properties
    properties.forEach((item) => {
      const creator = item.createdBy || "Unknown";
      const status = item.status || "active";
      if (!creatorStats[creator]) {
        creatorStats[creator] = {
          products: 0,
          properties: 0,
          ...(companyData?.entityType !== "hoa" ? { parking: 0 } : {}),
        };
      }
      creatorStats[creator].properties++;
      if (statusStats[status]) {
        statusStats[status].properties++;
      }
    });

    // Count parking spaces only if not HOA
    if (companyData?.entityType !== "hoa") {
      parkingSpaces.forEach((item) => {
        const creator = item.createdBy || "Unknown";
        const status = item.status || "active";
        if (!creatorStats[creator]) {
          creatorStats[creator] = { products: 0, properties: 0, parking: 0 };
        }
        creatorStats[creator].parking++;
        if (statusStats[status]) {
          statusStats[status].parking++;
        }
      });
    }

    // Get monthly trends
    const monthlyTrends = {};
    const getMonthFromDate = (dateString) => {
      try {
        const date = new Date(dateString);
        return date.toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
      } catch {
        return "Unknown";
      }
    };

    const itemsToProcess =
      companyData?.entityType === "hoa"
        ? [...products, ...properties]
        : [...products, ...properties, ...parkingSpaces];

    itemsToProcess.forEach((item) => {
      const month = getMonthFromDate(item.createdAt);
      if (!monthlyTrends[month]) {
        monthlyTrends[month] = {
          products: 0,
          properties: 0,
          ...(companyData?.entityType !== "hoa" ? { parking: 0 } : {}),
        };
      }
      if (item.collection === "products") monthlyTrends[month].products++;
      if (item.collection === "properties") monthlyTrends[month].properties++;
      if (
        companyData?.entityType !== "hoa" &&
        item.collection === "parkingSpaces"
      ) {
        monthlyTrends[month].parking++;
      }
    });

    return {
      creatorStats: Object.entries(creatorStats)
        .map(([creator, stats]) => ({
          creator,
          total:
            stats.products +
            stats.properties +
            (companyData?.entityType !== "hoa" ? stats.parking : 0),
          ...stats,
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5),
      monthlyTrends: Object.entries(monthlyTrends)
        .map(([month, stats]) => ({
          month,
          total:
            stats.products +
            stats.properties +
            (companyData?.entityType !== "hoa" ? stats.parking : 0),
          ...stats,
        }))
        .sort((a, b) => {
          const dateA = new Date(a.month);
          const dateB = new Date(b.month);
          return dateB - dateA;
        })
        .slice(0, 6),
      statusStats,
    };
  };

  // Render analytics modal
  const renderAnalyticsModal = () => {
    if (!showAnalytics) return null;

    const { creatorStats, monthlyTrends, statusStats } = getAnalyticsData();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-[#363062]">
              Marketplace Analytics
            </h3>
            <button
              onClick={() => setShowAnalytics(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-{companyData?.entityType === 'hoa' ? '3' : '4'} gap-4 mb-6">
            <div className="bg-[#363062] bg-opacity-10 p-4 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">Total Listings</p>
              <p className="text-lg font-semibold text-[#363062]">
                {products.length +
                  properties.length +
                  (companyData?.entityType !== "hoa"
                    ? parkingSpaces.length
                    : 0)}
              </p>
            </div>
            <div className="bg-[#363062] bg-opacity-10 p-4 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">Products</p>
              <p className="text-lg font-semibold text-[#363062]">
                {products.length}
              </p>
            </div>
            <div className="bg-[#8FCD00] bg-opacity-10 p-4 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">Properties</p>
              <p className="text-lg font-semibold text-[#8FCD00]">
                {properties.length}
              </p>
            </div>
            {companyData?.entityType !== "hoa" && (
              <div className="bg-[#363062] bg-opacity-10 p-4 rounded-lg text-center">
                <p className="text-xs text-gray-500 mb-1">Parking</p>
                <p className="text-lg font-semibold text-[#363062]">
                  {parkingSpaces.length}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Overview */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium mb-4 text-gray-700">
                Status Overview
              </h4>
              <div className="space-y-4">
                {Object.entries(statusStats).map(([status, stats]) => (
                  <div
                    key={status}
                    className="bg-white p-3 rounded-md shadow-sm"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium capitalize">
                        {status}
                      </span>
                      <span className="text-xs text-gray-500">
                        Total:{" "}
                        {stats.products +
                          stats.properties +
                          (companyData?.entityType !== "hoa"
                            ? stats.parking
                            : 0)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="font-medium">{stats.products}</div>
                        <div className="text-gray-500">Products</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium">{stats.properties}</div>
                        <div className="text-gray-500">Properties</div>
                      </div>
                      {companyData?.entityType !== "hoa" && (
                        <div className="text-center">
                          <div className="font-medium">{stats.parking}</div>
                          <div className="text-gray-500">Parking</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Trends */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium mb-4 text-gray-700">
                Monthly Trends
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-2 rounded-l-md">Month</th>
                      <th className="text-right p-2">Products</th>
                      <th className="text-right p-2">Properties</th>
                      {companyData?.entityType !== "hoa" && (
                        <th className="text-right p-2">Parking</th>
                      )}
                      <th className="text-right p-2 rounded-r-md">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyTrends.map((item, i) => (
                      <tr key={i} className="border-b border-gray-200">
                        <td className="p-2">{item.month}</td>
                        <td className="text-right p-2">{item.products}</td>
                        <td className="text-right p-2">{item.properties}</td>
                        {companyData?.entityType !== "hoa" && (
                          <td className="text-right p-2">{item.parking}</td>
                        )}
                        <td className="text-right p-2 font-medium">
                          {item.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top Contributors */}
            <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
              <h4 className="text-sm font-medium mb-4 text-gray-700">
                Top Contributors
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-2 rounded-l-md">User</th>
                      <th className="text-right p-2">Products</th>
                      <th className="text-right p-2">Properties</th>
                      {companyData?.entityType !== "hoa" && (
                        <th className="text-right p-2">Parking</th>
                      )}
                      <th className="text-right p-2 rounded-r-md">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creatorStats.map((item, i) => (
                      <tr key={i} className="border-b border-gray-200">
                        <td className="p-2 truncate max-w-[200px]">
                          {item.creator}
                        </td>
                        <td className="text-right p-2">{item.products}</td>
                        <td className="text-right p-2">{item.properties}</td>
                        {companyData?.entityType !== "hoa" && (
                          <td className="text-right p-2">{item.parking}</td>
                        )}
                        <td className="text-right p-2 font-medium">
                          {item.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render stats/summary cards
  const renderStatCards = () => {
    const activeProducts = products.filter(
      (p) => (p.status || "active") === "active"
    ).length;
    const activeProperties = properties.filter(
      (p) => (p.status || "active") === "active"
    ).length;
    const activeParking = parkingSpaces.filter(
      (p) => (p.status || "active") === "active"
    ).length;

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div
          className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-[#363062] cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleCardClick(1)}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-gray-500">Products</p>
              <p className="text-lg font-semibold">{activeProducts}</p>
            </div>
            <div className="bg-[#363062] bg-opacity-10 p-2 rounded-full">
              <LuShoppingBasket className="text-[#363062] text-xl" />
            </div>
          </div>
        </div>

        <div
          className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-[#8FCD00] cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleCardClick(2)}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-gray-500">Properties</p>
              <p className="text-lg font-semibold">{activeProperties}</p>
            </div>
            <div className="bg-[#8FCD00] bg-opacity-10 p-2 rounded-full">
              <LuDoorOpen className="text-[#8FCD00] text-xl" />
            </div>
          </div>
        </div>

        {companyData?.entityType !== "hoa" && (
          <div
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-[#363062] cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleCardClick(3)}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-500">Parking Spaces</p>
                <p className="text-lg font-semibold">{activeParking}</p>
              </div>
              <div className="bg-[#363062] bg-opacity-10 p-2 rounded-full">
                <FaSquareParking className="text-[#363062] text-xl" />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render item detail modal
  const renderItemDetail = () => {
    if (!itemDetail) return null;

    const { images = [], createdName, createdBy, createdAt } = itemDetail;
    const title = itemDetail.title || itemDetail.productTitle || "No Title";
    const price = itemDetail.price || "N/A";

    return (
      <PageModal>
        <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg overflow-hidden my-8">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-[#363062]">Item Details</h3>
            <button
              onClick={() => setShowItemDetail(false)}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              <FaTimes />
            </button>
          </div>

          <div className="p-6">
            {/* Image Gallery */}
            <div className="mb-6">
              {images && images.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 h-64">
                    <img
                      src={images[0]}
                      alt={title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                  {images.slice(1, 5).map((img, index) => (
                    <div key={index} className="h-32">
                      <img
                        src={img}
                        alt={`${title} ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-64 bg-gray-100 flex items-center justify-center rounded-lg">
                  <p className="text-gray-400">No images available</p>
                </div>
              )}
            </div>

            {/* Item Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <h2 className="text-xl font-semibold text-gray-800 mb-3">
                  {title}
                </h2>

                <div className="mb-4">
                  <p className="text-gray-600">
                    {itemDetail.description || "No description available."}
                  </p>
                </div>

                {/* Item Details */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {itemDetail.location && (
                    <div>
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-sm font-medium">
                        {itemDetail.location}
                      </p>
                    </div>
                  )}

                  {itemDetail.size && (
                    <div>
                      <p className="text-xs text-gray-500">Size</p>
                      <p className="text-sm font-medium">{itemDetail.size}</p>
                    </div>
                  )}

                  {itemDetail.rentalType && (
                    <div>
                      <p className="text-xs text-gray-500">Rental Type</p>
                      <p className="text-sm font-medium">
                        {itemDetail.rentalType}
                      </p>
                    </div>
                  )}

                  {itemDetail.categories &&
                    itemDetail.categories.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500">Categories</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {itemDetail.categories.map((cat, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-gray-100 px-2 py-1 rounded"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  {itemDetail.type && (
                    <div>
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="text-sm font-medium">{itemDetail.type}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="mb-4">
                  <p className="text-lg font-bold text-[#363062]">₱{price}</p>
                  <p className="text-xs text-gray-500">
                    {activeCategory === 3
                      ? itemDetail.rentalType || "Rental"
                      : activeCategory === 2
                      ? "Property Price"
                      : "Product Price"}
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs text-gray-500 mb-1">Listed by</p>
                  <p className="text-sm font-medium">
                    {createdName || "Anonymous"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {createdBy || "No email"}
                  </p>
                </div>

                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-1">Listed on</p>
                  <p className="text-sm">{createdAt || "Unknown date"}</p>
                </div>

                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      itemDetail.status === "active" || !itemDetail.status
                        ? "bg-green-100 text-green-800"
                        : itemDetail.status === "suspended"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {itemDetail.status || "active"}
                  </span>
                </div>

                <div className="mt-6 flex flex-col gap-2">
                  <button
                    onClick={() => {
                      handleApprove(itemDetail);
                      setShowItemDetail(false);
                    }}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-md text-sm hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FaCheck /> Approve Listing
                  </button>
                  <button
                    onClick={() => {
                      handleSuspend(itemDetail);
                      setShowItemDetail(false);
                    }}
                    className="w-full bg-yellow-500 text-white py-2 px-4 rounded-md text-sm hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <FaPause /> Suspend Listing
                  </button>
                  <button
                    onClick={() => {
                      setShowItemDetail(false);
                      handleDelete(itemDetail);
                    }}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-md text-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FaTrashAlt /> Delete Listing
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageModal>
    );
  };

  // Render table with data
  const renderTable = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#363062]"></div>
        </div>
      );
    }

    if (filteredData.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <FaExclamationTriangle className="text-yellow-500 text-4xl mx-auto mb-4" />
          <p className="text-gray-600">No items found matching your criteria</p>
          <button
            onClick={() => {
              setSearchTerm("");
              setFilterStatus("all");
            }}
            className="mt-4 text-[#363062] flex items-center gap-2 mx-auto"
          >
            <HiOutlineRefresh /> Reset filters
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => {
                    // Prevent row click when clicking on action buttons
                    if (
                      e.target.closest("button") ||
                      e.target.closest(".actions-cell")
                    ) {
                      return;
                    }
                    handleItemClick(item);
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-md object-cover"
                          src={
                            item.images && item.images.length > 0
                              ? item.images[0]
                              : "https://via.placeholder.com/40"
                          }
                          alt=""
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {item.title || item.productTitle}
                        </div>
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {item.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {item.createdName || "Anonymous"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.createdBy}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.createdAt}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.status === "active" || !item.status
                          ? "bg-green-100 text-green-800"
                          : item.status === "suspended"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {item.status || "active"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium actions-cell">
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(item);
                        }}
                        className="text-green-600 hover:text-green-900"
                        title="Approve"
                      >
                        <FaCheck />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSuspend(item);
                        }}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Suspend"
                      >
                        <FaPause />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="text-gray-600 text-sm max-w-7xl mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-[#363062]">
          Marketplace Admin
        </h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowAnalytics(true)}
            className="bg-[#363062] text-white px-3 py-1.5 rounded-md text-xs flex items-center gap-1 hover:bg-opacity-90 transition-colors"
          >
            <IoStatsChart size={14} /> Analytics
          </button>
        </div>
      </div>

      {renderStatCards()}

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`${
                activeCategory === category.id
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              } px-4 py-2 rounded-md text-xs flex items-center gap-2 transition-colors`}
            >
              {category.icon} {category.name}
            </button>
          ))}
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-2">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#363062] text-xs"
              placeholder={`Search ${
                categories.find((c) => c.id === activeCategory)?.name
              }...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="relative w-full md:w-48">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiFilter className="text-gray-400" />
            </div>
            <select
              className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#363062] text-xs appearance-none bg-white"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </div>
      </div>

      {renderTable()}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Deletion
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this item? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {renderAnalyticsModal()}

      {/* Item Detail Modal */}
      {showItemDetail && renderItemDetail()}
    </div>
  );
}

export default MarketplaceAdmin;
